import React, { useState } from "react";
import { Dropdown, Button, Modal, message, Tooltip, Input } from "antd";
import { MoreOutlined, EyeOutlined, EditOutlined, DeleteOutlined, TagsOutlined, ToolOutlined, PlayCircleOutlined, StopOutlined, EnvironmentOutlined, HistoryOutlined, ExclamationCircleOutlined, ExportOutlined, ImportOutlined, PictureOutlined, UndoOutlined, WarningOutlined, RollbackOutlined } from "@ant-design/icons";
import { uldService, uldDeliveryService } from "../../../../api/enhancedApi";
import { ULD_CORE_STATUS, getAvailableActions, getLocationUpdateBlockReason } from "../constants/uldConstantsV3";
import UldLocationUpdateModal from "./UldLocationUpdateModal";
import UldTagManageModal from "./UldTagManageModal";
import UldDetailModal from "./UldDetailModal";
import UldDeliveryModal from "./UldDeliveryModal";
import UldPhotoModal from "./UldPhotoModal";
import UldRecoverModal from "./UldRecoverModal";

import { useAuth } from "../../../../hooks/useAuth";

const { confirm } = Modal;
const { TextArea } = Input;

const UldActionDropdownV3 = ({ record, onRefresh, onEdit, onViewDetail, onViewHistory, onManageTags }) => {
    const [loading, setLoading] = useState(false);
    const [locationModalVisible, setLocationModalVisible] = useState(false);
    const [tagModalVisible, setTagModalVisible] = useState(false);
    const [detailModalVisible, setDetailModalVisible] = useState(false);
    const [deliveryModalVisible, setDeliveryModalVisible] = useState(false);
    const [deliveryModalMode, setDeliveryModalMode] = useState('deliver'); // 'deliver' | 'revoke'
    const [photoModalVisible, setPhotoModalVisible] = useState(false);
    const [recoverModalVisible, setRecoverModalVisible] = useState(false);
    const [lostModalAction, setLostModalAction] = useState(null); // 标记丢失（原因必填）的待提交动作
    const [lostReason, setLostReason] = useState("");

    // 获取用户权限
    const { hasPermission } = useAuth();

    // 获取ULD基本信息
    const uldId = record.master.id;
    const currentCoreStatus = record.stock?.core_status || "IN_STOCK";
    const currentAvailability = record.stock?.availability || "AVAILABLE";
    const isDelivered = record.master?.delivered_to_consignee;

    // 权限检查
    const canRead = hasPermission("uld:read");
    const canUpdate = hasPermission("uld:update");
    const canDelete = hasPermission("uld:delete");
    const canStatusUpdate = hasPermission("uld:status_update");
    const canTagManage = hasPermission("uld:tag:manage");
    const canViewHistory = hasPermission("uld:history:read");
    const canBatchUpdate = hasPermission("uld:batch_update");
    const canDeliver = hasPermission("uld:batch_update"); // 外放功能现在使用批量更新权限

    // 核心状态操作方法
    const handleCoreStatusChange = async (action, actionName) => {
        // 找回：必须选去向、填原因并确认在库类地点，走专用弹窗
        if (action.isRecover) {
            setRecoverModalVisible(true);
            return;
        }
        // 标记丢失：原因必填，走带原因输入的弹窗
        if (action.needsReason) {
            setLostReason("");
            setLostModalAction(action);
            return;
        }

        // 获取确认消息（支持函数形式）
        const confirmMessage = typeof action.confirmMessage === "function" ? action.confirmMessage(currentCoreStatus) : action.confirmMessage;

        confirm({
            title: "确认状态变更",
            icon: <ExclamationCircleOutlined />,
            content: confirmMessage,
            okText: "确定",
            cancelText: "取消",
            onOk: async () => {
                setLoading(true);
                try {
                    // TODO: 这里需要调用新的核心状态更新API
                    const response = await uldService.updateSingleUldCoreStatus(uldId, {
                        new_core_status: action.targetStatus,
                        remarks: `通过操作列${actionName}`,
                    });

                    if (response.data?.success) {
                        message.success(response.data.message);
                        onRefresh?.();
                    } else {
                        message.error(response.data?.message || `${actionName}失败`);
                    }
                } catch (error) {
                    console.error(`${actionName}失败:`, error);
                    message.error(error.response?.data?.detail || `${actionName}失败`);
                } finally {
                    setLoading(false);
                }
            },
        });
    };

    // 标记丢失提交（原因必填）
    const handleMarkLostSubmit = async () => {
        const reason = lostReason.trim();
        if (!reason) {
            message.error("必须填写原因");
            return;
        }
        const action = lostModalAction;
        setLoading(true);
        try {
            const response = await uldService.updateSingleUldCoreStatus(uldId, {
                new_core_status: action.targetStatus,
                remarks: reason,
            });
            if (response.data?.success) {
                message.success(response.data.message || "已标记丢失");
                setLostModalAction(null);
                onRefresh?.();
            } else {
                message.error(response.data?.message || "标记丢失失败");
            }
        } catch (error) {
            console.error("标记丢失失败:", error);
            message.error(error.response?.data?.detail || "标记丢失失败");
        } finally {
            setLoading(false);
        }
    };

    // 位置更新方法（自动触发状态变更）
    const handleLocationUpdate = () => {
        setLocationModalVisible(true);
    };

    // 删除操作
    const handleDelete = () => {
        confirm({
            title: "确认删除",
            icon: <ExclamationCircleOutlined />,
            content: `确定要删除ULD "${record.master.uld_identifier}" 吗？此操作不可撤销。`,
            okText: "确定",
            okType: "danger",
            cancelText: "取消",
            onOk: async () => {
                setLoading(true);
                try {
                    const response = await uldService.deleteUldMaster(uldId);
                    if (response.data?.success) {
                        message.success("ULD删除成功");
                        onRefresh?.();
                    } else {
                        message.error(response.data?.message || "ULD删除失败");
                    }
                } catch (error) {
                    console.error("删除ULD失败:", error);
                    message.error(error.response?.data?.detail || "删除ULD失败");
                } finally {
                    setLoading(false);
                }
            },
        });
    };

    // 外放操作处理
    const handleDeliveryOperation = (mode) => {
        setDeliveryModalMode(mode);
        setDeliveryModalVisible(true);
    };

    const handleDeliveryConfirm = async (data) => {
        setLoading(true);
        try {
            if (deliveryModalMode === 'deliver') {
                await uldDeliveryService.deliverToConsignee({
                    id: uldId,
                    data: data // 直接传递data，因为UldDeliveryModal已经格式化好了
                });
                message.success('ULD已成功外放');
            } else {
                await uldDeliveryService.revokeDelivery({
                    id: uldId,
                    data: data // 直接传递data，因为UldDeliveryModal已经格式化好了
                });
                message.success('ULD外放状态已撤销');
            }
            
            setDeliveryModalVisible(false);
            onRefresh?.();
        } catch (error) {
            console.error('外放操作失败:', error);
            message.error(error.response?.data?.detail || '操作失败');
        } finally {
            setLoading(false);
        }
    };

    // 单个ULD导出功能已移除

    // 构建菜单项
    const menuItems = [];

    // 基础操作 - 查看详情（需要读取权限）
    if (canRead) {
        menuItems.push({
            key: "view-detail",
            icon: <EyeOutlined />,
            label: "查看详情",
            onClick: () => setDetailModalVisible(true),
        });
    }

    // 编辑基础信息（需要更新权限）
    if (canUpdate) {
        menuItems.push({
            key: "edit",
            icon: <EditOutlined />,
            label: "编辑基础信息",
            onClick: () => onEdit?.(record),
        });
    }

    // 核心操作分隔线
    if (menuItems.length > 0 && (canBatchUpdate || canTagManage)) {
        menuItems.push({ type: "divider" });
    }

    // 位置更新操作（需要批量更新权限）
    // 外放须先撤销；丢失须先找回；停用/退役也不能普通改位置（与批量改位置前置检查同一口径）
    const locationBlockReason = getLocationUpdateBlockReason(
        currentCoreStatus,
        isDelivered,
        record.master?.consignee_name
    );
    if (canBatchUpdate) {
        menuItems.push({
            key: "update-location",
            icon: <EnvironmentOutlined />,
            disabled: !!locationBlockReason,
            label: locationBlockReason ? (
                <Tooltip title={locationBlockReason} placement="left">
                    <span>更新位置</span>
                </Tooltip>
            ) : (
                "更新位置"
            ),
            onClick: locationBlockReason ? undefined : handleLocationUpdate,
        });
    }

    // 标签管理操作（需要标签管理权限）
    if (canTagManage) {
        menuItems.push({
            key: "manage-tags",
            icon: <TagsOutlined />,
            label: "管理标签",
            onClick: () => setTagModalVisible(true),
        });
    }

    // 照片管理操作（需要更新权限，不限状态）
    if (canUpdate) {
        menuItems.push({
            key: "manage-photos",
            icon: <PictureOutlined />,
            label: "管理照片",
            onClick: () => setPhotoModalVisible(true),
        });
    }

    // 外放管理操作（需要批量更新权限）
    if (canDeliver) {
        // 添加分隔线（如果前面有其他操作）
        if (menuItems.length > 0) {
            menuItems.push({ type: "divider" });
        }

        if (isDelivered) {
            // 已外放状态：显示撤销外放
            menuItems.push({
                key: "revoke-delivery",
                icon: <ImportOutlined />,
                label: "撤销外放",
                onClick: () => handleDeliveryOperation('revoke'),
            });
        } else {
            // 未外放状态：显示外放操作（仅IN_STOCK状态可用）
            if (currentCoreStatus === 'IN_STOCK') {
                menuItems.push({
                    key: "deliver",
                    icon: <ExportOutlined />,
                    label: "标记外放",
                    onClick: () => handleDeliveryOperation('deliver'),
                });
            }
        }
    }

    // 导出操作已移除 - 仅保留页面顶部的整体导出功能

    // 状态管理操作（根据当前核心状态显示，需要状态更新权限）
    // 外放中的板不提供「标记丢失」：外放不是丢失，须先撤销外放（服务端同样拦截）
    const availableActions = getAvailableActions(currentCoreStatus).filter(
        (action) => !(action.key === "MARK_LOST" && isDelivered)
    );
    if (canStatusUpdate && availableActions.length > 0) {
        // 添加分隔线（如果前面有其他操作）
        if (menuItems.length > 0) {
            menuItems.push({ type: "divider" });
        }

        availableActions.forEach((action) => {
            const iconMap = {
                StopOutlined: <StopOutlined />,
                PlayCircleOutlined: <PlayCircleOutlined />,
                DeleteOutlined: <DeleteOutlined />,
                UndoOutlined: <UndoOutlined />,
                WarningOutlined: <WarningOutlined />,
                RollbackOutlined: <RollbackOutlined />,
            };

            menuItems.push({
                key: action.key,
                icon: iconMap[action.icon] || <ToolOutlined />,
                label: action.label,
                onClick: () => handleCoreStatusChange(action, action.label),
                danger: action.isDangerous || false,
            });
        });
    }

    // 高级操作分隔线
    const hasAdvancedOperations = canViewHistory || canDelete;
    if (hasAdvancedOperations && menuItems.length > 0) {
        menuItems.push({ type: "divider" });
    }

    // 查看历史操作（需要历史查看权限）
    if (canViewHistory) {
        menuItems.push({
            key: "view-history",
            icon: <HistoryOutlined />,
            label: "查看历史",
            onClick: () => onViewHistory?.(record),
        });
    }

    // 删除操作（需要删除权限）
    if (canDelete) {
        menuItems.push({
            key: "delete",
            icon: <DeleteOutlined />,
            label: "删除ULD",
            onClick: handleDelete,
            danger: true,
        });
    }

    return (
        <>
            <Dropdown
                menu={{
                    items: menuItems,
                }}
                trigger={["click"]}
                disabled={loading}
            >
                <Button type="text" icon={<MoreOutlined />} loading={loading} size="small" />
            </Dropdown>

            {/* 位置更新弹窗 */}
            <UldLocationUpdateModal
                visible={locationModalVisible}
                onCancel={() => setLocationModalVisible(false)}
                onSuccess={() => {
                    setLocationModalVisible(false);
                    onRefresh?.();
                }}
                record={record}
            />

            {/* 标签管理弹窗 */}
            <UldTagManageModal
                visible={tagModalVisible}
                onCancel={() => setTagModalVisible(false)}
                onSuccess={() => {
                    setTagModalVisible(false);
                    onRefresh?.();
                }}
                record={record}
            />

            {/* 详情查看弹窗 */}
            <UldDetailModal visible={detailModalVisible} onCancel={() => setDetailModalVisible(false)} uldId={uldId} />

            {/* 外放管理弹窗 */}
            <UldDeliveryModal
                visible={deliveryModalVisible}
                onCancel={() => setDeliveryModalVisible(false)}
                onConfirm={handleDeliveryConfirm}
                loading={loading}
                uld={record}
                mode={deliveryModalMode}
            />

            {/* 照片管理弹窗 */}
            <UldPhotoModal
                visible={photoModalVisible}
                onCancel={() => setPhotoModalVisible(false)}
                onSuccess={() => {
                    setPhotoModalVisible(false);
                    onRefresh?.();
                }}
                record={record}
            />

            {/* 标记丢失弹窗（原因必填） */}
            <Modal
                title={`标记丢失 ${record.master?.uld_identifier || ""}`}
                open={!!lostModalAction}
                onOk={handleMarkLostSubmit}
                onCancel={() => setLostModalAction(null)}
                okText="确认标记丢失"
                okButtonProps={{ danger: true, disabled: !lostReason.trim() }}
                cancelText="取消"
                confirmLoading={loading}
                destroyOnHidden
            >
                <p>{lostModalAction?.confirmMessage}</p>
                <TextArea
                    placeholder="必须填写原因：例如航班到站清点缺少、仓库盘点未发现"
                    rows={3}
                    showCount
                    maxLength={500}
                    value={lostReason}
                    onChange={(e) => setLostReason(e.target.value)}
                />
            </Modal>

            {/* 找回弹窗（选去向、填原因、确认在库类地点） */}
            <UldRecoverModal
                visible={recoverModalVisible}
                onCancel={() => setRecoverModalVisible(false)}
                onSuccess={() => {
                    setRecoverModalVisible(false);
                    onRefresh?.();
                }}
                record={record}
            />
        </>
    );
};

export default UldActionDropdownV3;
